import { LayerKey } from '@festina-life/api'
import type { Except } from '@festina-life/core'
import { RadioGroup } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo, useState } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { DEFAULT_BLOCK } from '../../system/constants'
import { useNavigationParams } from '../../system/useNavigationParams'
import { EnumDraftCreationForm } from '../createEnumDraft/EnumDraftCreationForm'
import { GlMapDraftCreationForm } from '../createGlMapDraft/GlMapDraftCreationForm'
import { ParameterDraftCreationForm } from '../createParameterDraft/ParameterDraftCreationForm'
import { ReconciliationSpecExcelUploadForm } from '../createReconciliationSpecDraft/ReconciliationSpecExcelUploadForm'
import { TemplateDraftCreationForm } from '../createTemplateDraft/TemplateDraftCreationForm'
import { TenantDraftCreationForm } from '../createTenantDraft/TenantDraftCreationForm'
import { DraftLocationSelector } from '../draftLocationSelectors/DraftLocationSelector'
import { DraftCreationFormId } from './DraftCreationModal'
import {
  type DraftCreationModalInitialValue,
  type DraftCreationProps,
  type DraftCreationType,
  DraftCreationTypes,
} from './types'

type Props = Except<
  DraftCreationProps<DraftCreationType>,
  'block' | 'initialValue'
> & {
  initialValue: DraftCreationModalInitialValue
}

export const NewDraftPicker = ({
  onStatusChanged,
  initialValue,
  layer,
}: Props) => {
  const { layerTopology } = useMetaModelContext()
  const { block } = useNavigationParams()
  const rootLayerKey = layerTopology
    ? new LayerKey({
        layerId: layerTopology?.name,
        tenantId: null,
      })
    : undefined
  const [selectedLayer, setSelectedLayer] = useState<LayerKey>(
    (layer.layerId ?? layer.tenantId) ? layer : (rootLayerKey ?? layer),
  )
  const [selectedBlock, setSelectedBlock] = useState<string>(
    block ?? DEFAULT_BLOCK,
  )
  const [formType, setFormType] = useState<DraftCreationType>(
    initialValue?.type ?? 'template',
  )
  const translate = useTranslate()

  const untypedValue =
    initialValue && initialValue.type === undefined
      ? initialValue.value
      : undefined

  const optionsTranslations = useMemo(
    () => ({
      parameterDefinition: translate('draftModal.type.parameterDefinition'),
      hideTemplate: translate('draftModal.type.hideTemplate'),
      template: translate('draftModal.type.template'),
      enumDefinition: translate('draftModal.type.enum'),
      tenant: translate('draftModal.type.tenant'),
      glMap: translate('draftModal.type.glMap'),
      reconciliationSpec: translate('draftModal.type.reconciliationSpec'),
    }),
    [translate],
  )

  return (
    <div className="flex flex-col gap-y-2 h-full">
      <DraftLocationSelector
        layer={selectedLayer}
        block={selectedBlock}
        onLayerChange={setSelectedLayer}
        onBlockChange={setSelectedBlock}
      />
      <RadioGroup
        wrapperProps={{
          label: undefined,
          description: translate('draftModal.picker.description'),
        }}
        options={DraftCreationTypes}
        renderFn={(value) => optionsTranslations[value]}
        isDisabled={!!initialValue?.type}
        onChange={setFormType}
        value={formType}
      />
      <div
        className="flex flex-col gap-y-2 h-full overflow-y-auto"
        key={`${selectedLayer.toString()}-${selectedBlock}`} // Forms needs to be re-mounted when the location changes
      >
        {formType === 'template' && (
          <TemplateDraftCreationForm
            initialValue={
              initialValue?.type === 'template'
                ? initialValue
                : untypedValue && { type: 'template', value: untypedValue }
            }
            layer={selectedLayer}
            block={selectedBlock}
            onStatusChanged={onStatusChanged}
          />
        )}
        {formType === 'enumDefinition' && (
          <EnumDraftCreationForm
            onStatusChanged={onStatusChanged}
            layer={selectedLayer}
            block={selectedBlock}
          />
        )}
        {formType === 'parameterDefinition' && (
          <ParameterDraftCreationForm
            initialValue={
              initialValue?.type === 'parameterDefinition'
                ? initialValue
                : untypedValue && {
                    type: 'parameterDefinition',
                    value: untypedValue,
                  }
            }
            onStatusChanged={onStatusChanged}
            layer={selectedLayer}
            block={selectedBlock}
          />
        )}
        {formType === 'tenant' && (
          <TenantDraftCreationForm
            onStatusChanged={onStatusChanged}
            layer={selectedLayer}
            block={selectedBlock}
          />
        )}
        {formType === 'glMap' && (
          <GlMapDraftCreationForm
            formId={DraftCreationFormId}
            initialValue={
              initialValue?.type === 'glMap' ? initialValue : undefined
            }
            onStatusChanged={onStatusChanged}
            layer={selectedLayer}
            block={selectedBlock}
          />
        )}
        {formType === 'reconciliationSpec' && (
          <ReconciliationSpecExcelUploadForm
            formId={DraftCreationFormId}
            onStatusChanged={onStatusChanged}
            layer={selectedLayer}
            block={selectedBlock}
          />
        )}
      </div>
    </div>
  )
}
